/*
=============================================
   TRAINING MATERIALS - RENDERER
=============================================
Pure HTML-building from the objects in training-content.js. No fetching, no
DOM wiring, no component lifecycle - both the in-app Training tab
(components/training.js) and the public page (scripts/training-page.js) call
these, which is what stops the signed-in and public copies drifting.

The `html` fields in the content file are TRUSTED, hand-authored markup that
lives in this repository, so they pass through as-is. Everything that reads as
a value rather than as prose (titles, URLs, attribution, file names) goes
through esc(), because those are the fields most likely to be pasted in from
somewhere else later by someone who is not thinking about markup that day.
*/

export function esc(s) {
   return String(s ?? "").replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
   ));
}

/*
Only http(s) links are allowed through. Every URL here is a YouTube link we
put in ourselves, so this is not defending against an attacker so much as
against a typo turning into a `javascript:` href years from now.
*/
function safeHref(url) {
   const s = String(url ?? "").trim();
   return /^https?:\/\//i.test(s) ? esc(s) : "#";
}

/* Turn "Ctrl Shift + arrow" into real <kbd> chrome without hand-writing tags
   in the content file. Splits on spaces, keeps "+" as a plain separator. */
function keys(combo) {
   return String(combo)
      .split(/\s+/)
      .map((part) => (part === "+" ? '<span class="tr-plus">+</span>' : `<kbd>${esc(part)}</kbd>`))
      .join(" ");
}

function videoList(items) {
   return `<ul class="tr-vids">${items
      .map(
         (v) => `<li>
            <a href="${safeHref(v.url)}" target="_blank" rel="noopener noreferrer">${esc(v.title)}</a>
            <span class="tr-by${v.mine ? " mine" : ""}">${esc(v.by)}</span>
         </li>`
      )
      .join("")}</ul>`;
}

/* Documents attached to a week. Unlike videoList these are usually LOCAL files
   served out of assets/, so they need an href guard that allows a relative path
   while still refusing anything that could become `javascript:`. */
function safeDocHref(s) {
   const v = String(s || "");
   return /^https?:\/\//i.test(v) || /^(assets\/|\/|#)/.test(v) ? esc(v) : "#";
}

/* A row with no `url` renders as PLAIN TEXT, not a dead link.
   That is not a nicety, it is forced by how student access works: the only route
   that serves an assignment file to a student is class-scoped
   (/me/classes/:classId/assignments), because being enrolled in that class is
   what entitles them to it. This page has no class context and a student may be
   in several, so it cannot build a working download link. Naming the workbook
   and saying where it lives beats a link that dead-ends. */
function docList(items) {
   return `<ul class="tr-docs">${items
      .map((d) => {
         const has = d.url && d.url !== "#";
         const external = /^https?:\/\//i.test(String(d.url || ""));
         const label = has
            ? `<a href="${safeDocHref(d.url)}"${external ? ' target="_blank" rel="noopener noreferrer"' : ""}>${esc(d.title)}</a>`
            : `<span class="tr-doc-title">${esc(d.title)}</span>`;
         return `<li>
            ${label}
            ${d.kind ? `<span class="tr-doc-kind">${esc(d.kind)}</span>` : ""}
            ${d.note ? `<span class="tr-doc-note">${esc(d.note)}</span>` : ""}
         </li>`;
      })
      .join("")}</ul>`;
}

function guideBlock(guide) {
   return `<div class="tr-guide">
      <p class="tr-guide-label">Guide</p>
      ${guide.map((g) => `<h4>${esc(g.h)}</h4>${g.html}`).join("")}
   </div>`;
}

function challengeCard(c) {
   const tagClass = c.tag && c.tag.kind === "has" ? "has" : "none";
   return `<article class="tr-card">
      <div class="tr-card-head">
         <span class="tr-num">${esc(c.num)}</span>
         <h3>${esc(c.name)}</h3>
         ${c.tag ? `<span class="tr-tag ${tagClass}">${esc(c.tag.text)}</span>` : ""}
      </div>
      ${c.file ? `<p class="tr-file">${esc(c.file)}</p>` : ""}
      ${c.videoLabel ? `<p class="tr-grouplabel">${esc(c.videoLabel)}</p>` : ""}
      ${c.videos ? videoList(c.videos) : ""}
      ${c.extraLabel ? `<p class="tr-grouplabel">${esc(c.extraLabel)}</p>` : ""}
      ${c.extra ? videoList(c.extra) : ""}
      ${c.guide ? guideBlock(c.guide) : ""}
      ${c.tip ? `<div class="tr-tip">${c.tip}</div>` : ""}
   </article>`;
}

/*
Unknown kinds return "" rather than throwing. A content file half way through
an edit should shorten this page, never blank it - the same reasoning as the
to_regclass guards on the server: degrade, do not 500.
*/
function section(s) {
   switch (s.kind) {
      case "note":
         return `<div class="tr-note">${s.html}</div>`;

      case "steps":
         return `<div class="tr-panel">
            <h2>${esc(s.title)}</h2>
            <div class="tr-steps">${s.items
               .map((it, i) => `<div class="tr-step"><span class="tr-n">${i + 1}</span><p>${it.html}</p></div>`)
               .join("")}</div>
         </div>`;

      case "keys":
         return `<div class="tr-panel">
            <h2>${esc(s.title)}</h2>
            <ul class="tr-cheat">${s.items
               .map((it) => `<li><span class="tr-k">${keys(it.k)}</span><span class="tr-d">${esc(it.d)}</span></li>`)
               .join("")}</ul>
            ${s.tip ? `<div class="tr-tip">${s.tip}</div>` : ""}
         </div>`;

      case "day":
         return `<section class="tr-day">
            <div class="tr-day-head">
               <h2>${esc(s.title)}</h2>
               ${s.focus ? `<span class="tr-focus">${esc(s.focus)}</span>` : ""}
            </div>
            ${s.cards.map(challengeCard).join("")}
         </section>`;

      /* One week of practice: what we did, and every document that came out of
         it. Kept as its own kind rather than squeezed into `day`, because a week
         is not a group of challenge cards and pretending otherwise would have
         meant filling in fields that mean nothing here. */
      case "week":
         return `<section class="tr-week">
            <div class="tr-week-head">
               <span class="tr-num">${esc(s.week)}</span>
               <h2>${esc(s.title)}</h2>
               ${s.date ? `<span class="tr-focus">${esc(s.date)}</span>` : ""}
            </div>
            ${s.html ? `<div class="tr-week-body">${s.html}</div>` : ""}
            ${s.docs ? docList(s.docs) : ""}
         </section>`;

      /* The function index. The filter is deliberately plain: it matches the
         function name AND the plain-English gloss, so a student who does not know
         a function is called SUBSTITUTE can still find it by typing "replace".
         Wired up by the caller after inject, since this module only builds HTML. */
      case "skillindex":
         return `<section class="tr-idx">
            <div class="tr-idx-head">
               <h2>${esc(s.title)}</h2>
               <input class="tr-filter" type="search" placeholder="${esc(s.placeholder || "Filter")}"
                      aria-label="${esc(s.placeholder || "Filter")}" autocomplete="off">
            </div>
            ${s.html ? `<div class="tr-idx-note">${s.html}</div>` : ""}
            <ul class="tr-skills">${s.items.map((it) => `
               <li class="tr-skill" data-find="${esc((it.skill + " " + (it.gloss || "") + " " + it.slug).toLowerCase())}">
                  <a href="${safeDocHref(it.href)}">
                     <span class="tr-skill-name">${esc(it.skill)}</span>
                     ${it.core ? "" : `<span class="tr-skill-basic" title="Everyday Excel, not a topic of its own">basic</span>`}
                  </a>
                  <span class="tr-skill-gloss">${esc(it.gloss || "")}</span>
                  <span class="tr-skill-where">${esc(it.where)}</span>
               </li>`).join("")}</ul>
            <p class="tr-idx-empty" hidden>Nothing matches that. Try a plainer word, "count", "look up", "text".</p>
         </section>`;

      /* One card per challenge: what it teaches, whether a beginner can open it,
         and the link to the full guide. */
      case "guides":
         return `<section class="tr-guides">
            <h2>${esc(s.title)}</h2>
            ${s.html ? `<div class="tr-idx-note">${s.html}</div>` : ""}
            <div class="tr-gwrap">${s.items.map((c) => `
               <article class="tr-gcard${c.open ? "" : " gated"}">
                  <div class="tr-gcard-top">
                     <a class="tr-gtitle" href="${safeDocHref(c.href)}">${esc(c.title)}</a>
                     <span class="tr-gtier">Tier ${esc(c.tier)}</span>
                  </div>
                  <p class="tr-gverdict">${esc(c.verdict)}</p>
                  <p class="tr-gteaches">${c.teaches.map((t) => `<code>${esc(t)}</code>`).join(" ")}</p>
                  <p class="tr-gmeta">${esc(c.questions)} questions &middot; ${esc(c.points)} points &middot; ${esc(c.levelCount)} levels${
                     c.pdfHref ? ` &middot; <a class="tr-gpdf" href="${safeDocHref(c.pdfHref)}" download>PDF</a>` : ""}</p>
               </article>`).join("")}</div>
         </section>`;

      /* Techniques rather than functions. These are the moves that transfer
         between cases, and they are the reason to browse rather than search. */
      case "concepts":
         return `<section class="tr-concepts">
            <h2>${esc(s.title)}</h2>
            ${s.html ? `<div class="tr-idx-note">${s.html}</div>` : ""}
            <ul class="tr-cons">${s.items.map((c) => `
               <li>
                  <a href="${safeDocHref(c.href)}">${esc(c.title)}</a>
                  <span>${esc(c.text)}</span>
               </li>`).join("")}</ul>
         </section>`;

      case "warn":
         return `<div class="tr-warn"><h2>${esc(s.title)}</h2>${s.html}</div>`;

      default:
         return "";
   }
}

/* The card grid. `href` is supplied by the caller so the in-app view can route
   through the hash and the public page can use a query string. `opts.note` is
   TRUSTED markup written by the two hosts (the "more is available" line under the
   grid when a visitor sees only part of the material, 2026-10-08). */
export function renderGrid(areas, hrefFor, opts = {}) {
   const cards = areas
      .map((a) => {
         // `a.href` is an area that lives elsewhere in the portal rather than in
         // the content file: the Competition Case Library is a fetching view of
         // its own (2026-09-17), and a tile pointing at it keeps the Training
         // grid the one place a student looks for material.
         const ready = !!a.href || (Array.isArray(a.sections) && a.sections.length > 0);
         const stats = (a.stats || []).map((s) => `<span>${esc(s)}</span>`).join("");
         const inner = `
            <span class="tr-tile-icon">${a.icon || ""}</span>
            <span class="tr-kicker">${esc(a.kicker || "")}</span>
            <span class="tr-tile-title">${esc(a.title)}</span>
            <span class="tr-tile-sum">${esc(a.summary || "")}</span>
            ${stats ? `<span class="tr-stats">${stats}</span>` : ""}
            ${ready ? "" : `<span class="tr-soon">Coming soon</span>`}`;
         return ready
            ? `<a class="tr-tile" href="${esc(a.href || hrefFor(a))}">${inner}</a>`
            : `<div class="tr-tile is-soon">${inner}</div>`;
      })
      .join("");

   return `<div class="tr-root">
      <h1 class="tr-h1">Training materials</h1>
      <p class="tr-lede">Everything the team has built for getting better, in one place. Start anywhere.</p>
      <div class="tr-grid">${cards}</div>
      ${opts.note ? `<p class="tr-access-note">${opts.note}</p>` : ""}
   </div>`;
}

/* One area's full page. `backHref` is optional so the public page can omit it. */
export function renderArea(area, backHref) {
   return `<div class="tr-root">
      ${backHref ? `<a class="tr-back" href="${esc(backHref)}">&larr; All training materials</a>` : ""}
      <header class="tr-mast">
         <p class="tr-kicker">${esc(area.kicker || "")}</p>
         <div class="tr-rule"></div>
         <h1>${esc(area.title)}</h1>
         ${area.intro ? `<p>${esc(area.intro)}</p>` : ""}
      </header>
      ${(area.sections || []).map(section).join("")}
      ${area.footer ? `<footer class="tr-foot">${area.footer}</footer>` : ""}
   </div>`;
}

export function findArea(areas, slug) {
   return areas.find((a) => a.slug === slug) || null;
}

/*
Wire up the Skill Guides filter box. Called by BOTH hosts after they inject the
HTML, because this module builds strings and does not touch the DOM itself.

Safe to call on a page with no filter box: it finds nothing and returns. That
matters because both hosts call it unconditionally after rendering any area.
*/
export function wireFilter(root) {
   const box = root.querySelector(".tr-filter");
   if (!box) return;
   const items = Array.from(root.querySelectorAll(".tr-skill"));
   const empty = root.querySelector(".tr-idx-empty");
   /*
   Match on WORDS, not on the whole string as one substring.

   ⚠️ A plain `.includes(query)` looked fine and failed the exact case this box
   exists for. XLOOKUP's gloss reads "Look something up in any direction", so
   typing "look up" matched nothing, while the single word "lookup" did match
   (it is inside "xlookup"). A student who describes what they want in two words
   is the whole reason for searching the gloss rather than just the name.

   Every typed word must appear somewhere, in any order, so "up look" works too
   and a half-typed second word never wipes out the results from the first.
   */
   const apply = () => {
      const words = box.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
      let shown = 0;
      for (const li of items) {
         const hay = li.dataset.find;
         const hit = words.every((w) => hay.includes(w));
         li.hidden = !hit;
         shown += hit ? 1 : 0;
      }
      if (empty) empty.hidden = shown > 0;
   };
   box.addEventListener("input", apply);
   // Escape clears, because a filtered list with the box scrolled out of view is
   // otherwise a page that looks broken.
   box.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { box.value = ""; apply(); }
   });
}
